import { icons } from "./icons.js";
import { FORMATS, ALL_FORMAT_IDS, type FormatDescriptor } from "../formats.js";
import type { ScanSettings } from "../types.js";

const STYLE = /* css */ `
:host {
  display: block; width: 100%; height: 100%; overflow-y: auto;
  background: #f4f4f6; color: #16171b;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  --jsscan-accent: #d81f4b;
  box-sizing: border-box;
}
* { box-sizing: border-box; }
.header {
  position: sticky; top: 0; z-index: 2;
  display: flex; align-items: center; gap: 14px;
  background: #101114; color: #fff;
  padding: max(16px, env(safe-area-inset-top)) 16px 16px;
}
.header button {
  background: transparent; border: none; color: #fff; cursor: pointer;
  display: inline-flex; padding: 4px;
}
.header button svg { width: 22px; height: 22px; }
.header h1 { font-size: 17px; font-weight: 600; margin: 0; }
.content { padding: 18px 16px 40px; }
.eyebrow {
  font-size: 12px; font-weight: 700; letter-spacing: 0.06em; color: #7a7c85;
  text-transform: uppercase; margin: 22px 2px 8px;
}
.eyebrow:first-child { margin-top: 0; }
.card {
  background: #fff; border-radius: 14px; overflow: hidden;
  box-shadow: 0 1px 2px rgba(20,20,30,0.06);
}
.row {
  display: flex; align-items: center; justify-content: space-between;
  padding: 13px 16px; border-bottom: 1px solid #eef0f3;
  gap: 12px;
}
.row:last-child { border-bottom: none; }
.row-label { font-size: 14.5px; font-weight: 500; display: flex; align-items: center; gap: 10px; }
.row-label .row-icon { width: 18px; height: 18px; color: #4a4c56; flex: none; }
.badge {
  font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em;
  color: #a3a6b1; background: #eceef2; border-radius: 999px; padding: 2px 8px;
}
.switch {
  --w: 42px; --h: 24px;
  position: relative; width: var(--w); height: var(--h); flex: none;
  border-radius: var(--h); background: #e2e3e8; cursor: pointer; border: none; padding: 0;
  transition: background 0.15s ease;
}
.switch::after {
  content: ""; position: absolute; top: 2px; left: 2px; width: calc(var(--h) - 4px); height: calc(var(--h) - 4px);
  border-radius: 50%; background: #fff; box-shadow: 0 1px 3px rgba(0,0,0,0.25);
  transition: transform 0.15s ease;
}
.switch[aria-checked="true"] { background: var(--jsscan-accent); }
.switch[aria-checked="true"]::after { transform: translateX(calc(var(--w) - var(--h))); }
.switch:disabled { opacity: 0.4; cursor: not-allowed; }
.checkbox {
  width: 24px; height: 24px; border-radius: 7px; border: 2px solid #d3d5db;
  background: #fff; display: inline-flex; align-items: center; justify-content: center;
  cursor: pointer; flex: none; color: #fff;
}
.checkbox svg { width: 14px; height: 14px; display: none; }
.checkbox[aria-checked="true"] { background: var(--jsscan-accent); border-color: var(--jsscan-accent); }
.checkbox[aria-checked="true"] svg { display: block; }
.tabs {
  display: flex; background: #e7e8ec; border-radius: 12px; padding: 3px; margin: 4px 2px 14px;
}
.tab {
  flex: 1; text-align: center; padding: 9px 0; font-size: 13.5px; font-weight: 600;
  color: #5a5c66; background: transparent; border: none; border-radius: 10px; cursor: pointer;
}
.tab.active { background: #fff; color: #16171b; box-shadow: 0 1px 2px rgba(20,20,30,0.12); }
.section-title { font-size: 12px; font-weight: 700; letter-spacing: 0.06em; color: #7a7c85; text-transform: uppercase; margin: 0 0 8px 2px; }
`;

const SCAN_SETTING_ROWS: { key: keyof ScanSettings; label: string }[] = [
  { key: "multipleScanning", label: "Multiple Scanning" },
  { key: "viewFinder", label: "View Finder" },
  { key: "beepTone", label: "Beep Tone" },
  { key: "arOverlay", label: "AR Overlay" },
];

export class SettingsPanel extends HTMLElement {
  private readonly shadow: ShadowRoot;
  private contentEl!: HTMLDivElement;
  private _settings: ScanSettings | null = null;
  private activeCategory: "1d" | "2d" = "1d";

  constructor() {
    super();
    this.shadow = this.attachShadow({ mode: "open" });
    const style = document.createElement("style");
    style.textContent = STYLE;
    const header = document.createElement("div");
    header.className = "header";
    header.innerHTML = `<button data-action="back" aria-label="Back">${icons.back}</button><h1>Settings</h1>`;
    header.querySelector("button")!.addEventListener("click", () => this.dispatchEvent(new CustomEvent("back")));
    this.contentEl = document.createElement("div");
    this.contentEl.className = "content";
    this.shadow.append(style, header, this.contentEl);
  }

  set settings(value: ScanSettings) {
    this._settings = value;
    this.render();
  }
  get settings(): ScanSettings | null {
    return this._settings;
  }

  private emitChange(patch: Partial<ScanSettings>): void {
    this.dispatchEvent(new CustomEvent<Partial<ScanSettings>>("settings-change", { detail: patch }));
  }

  private render(): void {
    const s = this._settings;
    if (!s) return;
    this.contentEl.innerHTML = "";

    this.contentEl.appendChild(this.renderEyebrow("Scan Settings"));
    this.contentEl.appendChild(this.renderScanSettingsCard(s));

    this.contentEl.appendChild(this.renderEyebrow("Barcode Settings"));
    this.contentEl.appendChild(this.renderTabs());
    this.contentEl.appendChild(this.renderFormatCard(s));
  }

  private renderEyebrow(text: string): HTMLElement {
    const el = document.createElement("div");
    el.className = "eyebrow";
    el.textContent = text;
    return el;
  }

  private renderScanSettingsCard(s: ScanSettings): HTMLElement {
    const card = document.createElement("div");
    card.className = "card";
    for (const { key, label } of SCAN_SETTING_ROWS) {
      const row = document.createElement("div");
      row.className = "row";
      const value = Boolean(s[key]);
      row.innerHTML = `<span class="row-label">${label}</span>`;
      const sw = this.makeSwitch(value, (next) => this.emitChange({ [key]: next } as Partial<ScanSettings>));
      row.appendChild(sw);
      card.appendChild(row);
    }
    return card;
  }

  private renderTabs(): HTMLElement {
    const tabs = document.createElement("div");
    tabs.className = "tabs";
    for (const cat of ["1d", "2d"] as const) {
      const btn = document.createElement("button");
      btn.className = `tab${this.activeCategory === cat ? " active" : ""}`;
      btn.textContent = cat === "1d" ? "1D Barcodes" : "2D Barcodes";
      btn.addEventListener("click", () => {
        this.activeCategory = cat;
        this.render();
      });
      tabs.appendChild(btn);
    }
    return tabs;
  }

  private renderFormatCard(s: ScanSettings): HTMLElement {
    const card = document.createElement("div");
    card.className = "card";
    const formats = FORMATS.filter((f) => f.category === this.activeCategory);
    const categoryIds = formats.map((f) => f.id);
    // Only formats with a decoder behind them can actually be toggled, so
    // "all" is judged (and set) against that subset — otherwise the checkbox
    // could never show as checked while any "Soon" format exists.
    const selectableIds = formats.filter((f) => f.supported).map((f) => f.id);
    const allEnabled = selectableIds.length > 0 && selectableIds.every((id) => s.enabledFormats.includes(id));

    const enableAllRow = document.createElement("div");
    enableAllRow.className = "row";
    enableAllRow.innerHTML = `<span class="row-label">Enable all</span>`;
    enableAllRow.appendChild(
      this.makeCheckbox(allEnabled, (next) => {
        const withoutCategory = s.enabledFormats.filter((id) => !categoryIds.includes(id));
        this.emitChange({ enabledFormats: next ? [...withoutCategory, ...selectableIds] : withoutCategory });
      }),
    );
    card.appendChild(enableAllRow);

    for (const format of formats) {
      card.appendChild(this.renderFormatRow(format, s));
    }
    return card;
  }

  private renderFormatRow(format: FormatDescriptor, s: ScanSettings): HTMLElement {
    const row = document.createElement("div");
    row.className = "row";
    const label = document.createElement("span");
    label.className = "row-label";
    label.textContent = format.label;
    if (!format.supported) {
      const badge = document.createElement("span");
      badge.className = "badge";
      badge.textContent = "Soon";
      label.appendChild(badge);
    }
    row.appendChild(label);
    const enabled = s.enabledFormats.includes(format.id);
    const sw = this.makeSwitch(enabled, (next) => {
      const set = new Set(s.enabledFormats);
      next ? set.add(format.id) : set.delete(format.id);
      this.emitChange({ enabledFormats: [...set] });
    });
    if (!format.supported) sw.disabled = true;
    row.appendChild(sw);
    return row;
  }

  private makeSwitch(checked: boolean, onChange: (next: boolean) => void): HTMLButtonElement {
    const btn = document.createElement("button");
    btn.className = "switch";
    btn.type = "button";
    btn.role = "switch";
    btn.setAttribute("aria-checked", String(checked));
    btn.addEventListener("click", () => onChange(btn.getAttribute("aria-checked") !== "true"));
    return btn;
  }

  private makeCheckbox(checked: boolean, onChange: (next: boolean) => void): HTMLButtonElement {
    const btn = document.createElement("button");
    btn.className = "checkbox";
    btn.type = "button";
    btn.role = "checkbox";
    btn.innerHTML = icons.check;
    btn.setAttribute("aria-checked", String(checked));
    btn.addEventListener("click", () => onChange(btn.getAttribute("aria-checked") !== "true"));
    return btn;
  }
}

customElements.define("jsscan-settings", SettingsPanel);

export { ALL_FORMAT_IDS };
